import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Button,
  Alert,
  Stack,
  useTheme,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

const parseStructuredFaqContent = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

const normalizeLookupText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const StructuredColumnDefinitions = ({ content, theme, target }) => (
    <Stack spacing={1.5}>
      {Object.entries(content).map(([sectionTitle, sectionItems]) => (
        <Box
          key={sectionTitle}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              backgroundColor: theme.palette.grey[100],
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
              {sectionTitle}
            </Typography>
          </Box>
          <Stack spacing={0} sx={{ p: 1.25 }}>
            {Object.entries(sectionItems || {}).map(([columnName, columnDetails], index) => {
              const isTargetEntry =
                target?.sectionTitle === sectionTitle && target?.columnName === columnName;

              return (
                <Box
                  key={`${sectionTitle}-${columnName}`}
                  data-help-target={isTargetEntry ? 'true' : 'false'}
                  sx={{
                    py: 0.75,
                    px: isTargetEntry ? 0.75 : 0,
                    borderRadius: isTargetEntry ? 0.75 : 0,
                    backgroundColor: isTargetEntry ? theme.palette.action.selected : 'transparent',
                    borderBottom:
                      index === Object.entries(sectionItems || {}).length - 1
                        ? 'none'
                        : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>
                    {columnName}
                  </Typography>
                  {columnDetails?.description && (
                    <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                      {columnDetails.description}
                    </Typography>
                  )}
                  {columnDetails?.interaction?.onClick && (
                    <Typography
                      variant='caption'
                      sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}
                    >
                      On click: {columnDetails.interaction.onClick}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );

const HelpModal = ({ open, onClose, target = null }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.tsw?.primary || theme.palette.primary.main;
  const summaryBackground = lighten(primaryColor, 0.88);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPanel, setExpandedPanel] = useState(false);
  const accordionRefs = useRef({});
  const dialogContentRef = useRef(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      const fetchFaqs = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await axios.get('/suggestedOrders/faqs');

          const faqData = Array.isArray(response.data) ? response.data : response.data?.faqs || [];
          setFaqs(faqData);
        } catch (err) {
          console.error('Error fetching FAQs:', err);
          setError(
            err.response?.data?.message ||
              'Failed to load Frequently Asked Questions. Please try again later.'
          );
        } finally {
          setLoading(false);
        }
      };

      fetchFaqs();
    } else {
      setFaqs([]);
      setLoading(false);
      setError(null);
      setExpandedPanel(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !faqs.length || !target?.faqDescription) {
      return;
    }

    const normalizedTargetDescription = normalizeLookupText(target.faqDescription);
    const descriptionMatchedIndex = faqs.findIndex(
      (faq) => normalizeLookupText(faq.description) === normalizedTargetDescription,
    );

    const containsMatchedIndex =
      descriptionMatchedIndex !== -1
        ? descriptionMatchedIndex
        : faqs.findIndex((faq) =>
            normalizeLookupText(faq.description).includes(normalizedTargetDescription),
          );

    const structuredMatchedIndex =
      containsMatchedIndex !== -1
        ? containsMatchedIndex
        : faqs.findIndex((faq) => {
            const structuredContent = parseStructuredFaqContent(faq.detailed_description);
            if (!structuredContent || !target.sectionTitle) {
              return false;
            }

            const sectionMatch = Object.keys(structuredContent).find(
              (sectionTitle) =>
                normalizeLookupText(sectionTitle) === normalizeLookupText(target.sectionTitle),
            );

            if (!sectionMatch) {
              return false;
            }

            if (!target.columnName) {
              return true;
            }

            return Object.keys(structuredContent[sectionMatch] || {}).some(
              (columnName) =>
                normalizeLookupText(columnName) === normalizeLookupText(target.columnName),
            );
          });

    if (structuredMatchedIndex === -1) {
      return;
    }

    const panelId = `panel-${structuredMatchedIndex}`;
    setExpandedPanel(panelId);
  }, [faqs, open, target]);

  useEffect(() => {
    if (!open || !target || !expandedPanel) {
      return;
    }

    const getScrollContainer = (node) => {
      let current = node;
      while (current) {
        if (current.scrollHeight > current.clientHeight + 1) {
          return current;
        }
        current = current.parentElement;
      }
      return node;
    };

    const scrollNodeIntoContainer = (container, targetNode, offset = 20) => {
      const containerRect = container.getBoundingClientRect();
      const nodeRect = targetNode.getBoundingClientRect();
      const absoluteTop = container.scrollTop + (nodeRect.top - containerRect.top) - offset;
      container.scrollTop = Math.max(0, absoluteTop);
    };

    const runScrollPass = () => {
      const contentRoot = dialogContentRef.current;
      if (!contentRoot) return;

      const scrollContainer = getScrollContainer(contentRoot);
      const targetNode = contentRoot.querySelector('[data-help-target="true"]');
      if (targetNode) {
        scrollNodeIntoContainer(scrollContainer, targetNode, 24);
        return;
      }

      const panelNode = accordionRefs.current[expandedPanel];
      if (panelNode) {
        scrollNodeIntoContainer(scrollContainer, panelNode, 8);
      }
    };

    const timerA = setTimeout(runScrollPass, 260);
    const timerB = setTimeout(runScrollPass, 520);

    return () => {
      clearTimeout(timerA);
      clearTimeout(timerB);
    };
  }, [expandedPanel, open, target]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '85vh',
        },
      }}
      aria-labelledby='help-dialog-title'
    >
      <DialogTitle
        id='help-dialog-title'
        sx={{
          m: 0,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'relative',
        }}
      >
        <Stack direction='row' alignItems='center' sx={{ pr: 4 }}>
          <Typography
            variant='h6'
            component='div'
            sx={{
              fontWeight: 600,
              color: theme.palette.tsw?.primary || theme.palette.primary.main,
            }}
          >
            Frequently Asked Questions
          </Typography>
        </Stack>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          size='small'
          sx={{ position: 'absolute', top: 10, right: 10, p: 0.5 }}
        >
          <CloseIcon sx={{ color: theme.palette.delete.main, fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent ref={dialogContentRef} dividers sx={{ p: 0 }}>
        {' '}
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: theme.spacing(4),
            }}
          >
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading FAQs...</Typography>
          </Box>
        ) : error ? (
          <Alert severity='error' sx={{ m: theme.spacing(2) }}>
            {error}
          </Alert>
        ) : (
          <Stack spacing={1} sx={{ p: theme.spacing(2) }}>
            {' '}
            {faqs.length === 0 ? (
              <Typography
                sx={{ textAlign: 'center', p: theme.spacing(3), color: 'text.secondary' }}
              >
                No frequently asked questions are currently available.
              </Typography>
            ) : (
              faqs.map((faq, index) => (
                <Accordion
                  key={faq.faq_id || index}
                  ref={(node) => {
                    accordionRefs.current[`panel-${index}`] = node;
                  }}
                  expanded={expandedPanel === `panel-${index}`}
                  onChange={(_, isExpanded) => {
                    setExpandedPanel(isExpanded ? `panel-${index}` : false);
                  }}
                  square
                  disableGutters
                  elevation={0}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius,
                    overflow: 'hidden',
                    '&.Mui-expanded': {
                      margin: 0,
                    },
                    '&:not(:last-of-type)': {
                      marginBottom: theme.spacing(1),
                    },
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: primaryColor }} />}
                    aria-controls={`faq-content-${index}`}
                    id={`faq-header-${index}`}
                    sx={{
                      backgroundColor: summaryBackground,
                      color: primaryColor,
                      minHeight: 48,
                      overflow: 'hidden',
                      '&.Mui-expanded': {
                        minHeight: 48,
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: theme.spacing(1, 0),
                      },
                    }}
                  >
                    <Typography
                      variant='subtitle1'
                      sx={{ fontWeight: 700, color: primaryColor }}
                    >
                      {faq.description}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.default,
                    }}
                  >
                    <Stack spacing={1.5}>
                      {(() => {
                        const structuredContent = parseStructuredFaqContent(
                          faq.detailed_description,
                        );

                        if (structuredContent) {
                          return (
                            <StructuredColumnDefinitions
                              content={structuredContent}
                              theme={theme}
                              target={target}
                            />
                          );
                        }

                        return (
                          <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                            {faq.detailed_description}
                          </Typography>
                        );
                      })()}
                      {faq.button_text && faq.button_url && (
                        <Box>
                          {' '}
                          <Button
                            variant='outlined'
                            size='small'
                            href={faq.button_url}
                            target={faq.button_new_tab ? '_blank' : '_self'}
                            rel={faq.button_new_tab ? 'noopener noreferrer' : undefined}
                            sx={{
                              color: theme.palette.tsw?.primary || theme.palette.primary.main,
                              borderColor: theme.palette.tsw?.tertiary || theme.palette.divider,
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                                borderColor:
                                  theme.palette.tsw?.primary || theme.palette.primary.main,
                              },
                            }}
                          >
                            {faq.button_text}
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HelpModal;
